// components/TradingCommand/MarketChart.jsx - SIMPLIFIED WITH DIRECT BAR READING

import React, { memo, useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { Card, Badge, Button, ButtonGroup } from "react-bootstrap";

const MarketChart = ({ data, selectedPosition }) => {

  // ✅ EXTRACT ALL ADJUSTMENTS FROM ALL POSITIONS (show on main chart)
  const allAdjustmentsData = useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }
    
    const adjustmentsByPosition = {};
    
    // Find all bars with ADJUST decision across all positions
    data.forEach((bar, index) => {
      const barPositionId = bar.eval_position_id || bar.position_id;
      
      if (barPositionId && bar.eval_decision === 'ADJUST') {
        // Initialize tracking for this position if first time seeing it
        if (!adjustmentsByPosition[barPositionId]) {
          adjustmentsByPosition[barPositionId] = {
            adjustments: [],
            latestTarget: bar.tp || bar.target || 0,
            latestStop: bar.sl || bar.stop_loss || 0,
            originalTarget: bar.tp || bar.target || 0,
            originalStop: bar.sl || bar.stop_loss || 0
          };
        }
        
        const posData = adjustmentsByPosition[barPositionId];
        
        // Get the actual close price at this bar for Y-axis positioning
        const closePrice = parseFloat(bar.close || bar.c || 0);
        
        // Found an adjustment bar!
        const adjustment = {
          positionId: barPositionId,
          timestamp: new Date(bar.t).getTime(),
          oldTarget: parseFloat(bar.tp || bar.target || posData.latestTarget || 0),
          newTarget: parseFloat(bar.eval_new_target || bar.tp || bar.target || 0),
          oldStop: parseFloat(bar.sl || bar.stop_loss || posData.latestStop || 0),
          newStop: parseFloat(bar.eval_new_stop_loss || bar.sl || bar.stop_loss || 0),
          reason: bar.eval_pattern_influence || bar.eval_reasoning || 'Adjustment',
          price: closePrice,  // Use close price for marker positioning
          barIndex: index
        };
        
        posData.adjustments.push(adjustment);
        
        // Update latest values for this position
        if (adjustment.newTarget) posData.latestTarget = adjustment.newTarget;
        if (adjustment.newStop) posData.latestStop = adjustment.newStop;
        
        console.log(`📊 Found adjustment at ${bar.t} (index ${index}) for position ${barPositionId}:`, {
          timestamp: adjustment.timestamp,
          price: adjustment.price,
          newTarget: adjustment.newTarget,
          newStop: adjustment.newStop
        });
      }
    });
    
    // Flatten all adjustments into single array
    const allAdjustments = [];
    Object.values(adjustmentsByPosition).forEach(posData => {
      allAdjustments.push(...posData.adjustments);
    });
    
    console.log(`✅ Found ${allAdjustments.length} total adjustments across all positions:`, allAdjustments);
    
    return allAdjustments;
  }, [data]);

  // ✅ EXTRACT ADJUSTMENTS FOR SELECTED POSITION (if any)
  const selectedPositionData = useMemo(() => {
    if (!selectedPosition || !data || data.length === 0) {
      return { adjustments: [], currentTarget: null, currentStop: null };
    }
    
    const positionId = selectedPosition.id || selectedPosition.a_id;
    const adjustments = [];
    let latestTarget = selectedPosition.target || selectedPosition.tp || 0;
    let latestStop = selectedPosition.stop || selectedPosition.sl || 0;
    
    // Find all bars with ADJUST decision for this position
    data.forEach((bar, index) => {
      const barPositionId = bar.eval_position_id || bar.position_id;
      
      if (barPositionId === positionId && bar.eval_decision === 'ADJUST') {
        const adjustment = {
          timestamp: new Date(bar.t).getTime(),
          oldTarget: bar.tp || bar.target || latestTarget,
          newTarget: bar.eval_new_target || bar.tp || bar.target || latestTarget,
          oldStop: bar.sl || bar.stop_loss || latestStop,
          newStop: bar.eval_new_stop_loss || bar.sl || bar.stop_loss || latestStop,
          reason: bar.eval_pattern_influence || bar.eval_reasoning || 'Adjustment',
          price: parseFloat(bar.close || 0)
        };
        
        adjustments.push(adjustment);
        
        // Update latest values
        if (adjustment.newTarget) latestTarget = adjustment.newTarget;
        if (adjustment.newStop) latestStop = adjustment.newStop;
      }
    });
    
    return {
      adjustments,
      currentTarget: latestTarget,
      currentStop: latestStop,
      originalTarget: selectedPosition.target || selectedPosition.tp || 0,
      originalStop: selectedPosition.stop || selectedPosition.sl || 0
    };
  }, [selectedPosition, data]);

  // Prepare OHLC data
  const ohlc = useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) return [];
    
    return data.map(({ t, open, high, low, close }) => ({
      x: new Date(t).getTime(),
      y: [
        parseFloat(open) || 0, 
        parseFloat(high) || 0, 
        parseFloat(low) || 0, 
        parseFloat(close) || 0
      ],
    }));
  }, [data]);

  // Prepare prediction lines
  const up = useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) return [];
    
    return data.map(({ t, up }) => {
      if (!up || up === null || up === "null" || up === "NaN") {
        return { x: new Date(t).getTime(), y: null };
      }
      const yValue = parseFloat(up);
      if (isNaN(yValue) || yValue <= 0) {
        return { x: new Date(t).getTime(), y: null };
      }
      return { x: new Date(t).getTime(), y: yValue };
    });
  }, [data]);

  const down = useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) return [];
    
    return data.map(({ t, down }) => {
      if (!down || down === null || down === "null" || down === "NaN") {
        return { x: new Date(t).getTime(), y: null };
      }
      const yValue = parseFloat(down);
      if (isNaN(yValue) || yValue <= 0) {
        return { x: new Date(t).getTime(), y: null };
      }
      return { x: new Date(t).getTime(), y: yValue };
    });
  }, [data]);

  const pv = useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) return [];
    
    return data.map(({ t, pv }) => {
      if (!pv || pv === null || pv === "null" || pv === "NaN") {
        return { x: new Date(t).getTime(), y: null };
      }
      const yValue = parseFloat(pv);
      if (isNaN(yValue) || yValue <= 0) {
        return { x: new Date(t).getTime(), y: null };
      }
      return { x: new Date(t).getTime(), y: yValue };
    });
  }, [data]);

  // Get latest bar's genAI data
  const latestData = useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) return {};
    return data[data.length - 1] || {};
  }, [data]);
  
  const genAI = latestData.gen_ai_msg || {};

  // ✅ BUILD ANNOTATIONS with adjustments
  const positionAnnotations = useMemo(() => {
    const annotations = {
      yaxis: [],
      xaxis: [],
      points: []
    };

    // If no position selected, show genAI annotations
    if (!selectedPosition) {
      if (genAI.entry_price) {
        annotations.yaxis.push({
          y: parseFloat(genAI.entry_price),
          borderColor: '#FEB019',
          strokeDashArray: 0,
          label: {
            borderColor: '#FEB019',
            style: { color: '#000', background: '#FEB019', fontSize: '10px' },
            text: `Entry: $${parseFloat(genAI.entry_price).toFixed(2)}`
          }
        });
      }

      if (genAI.take_profit) {
        annotations.yaxis.push({
          y: parseFloat(genAI.take_profit),
          borderColor: '#00E396',
          strokeDashArray: 4,
          label: {
            borderColor: '#00E396',
            style: { color: '#000', background: '#00E396', fontSize: '10px' },
            text: `Target: $${parseFloat(genAI.take_profit).toFixed(2)}`
          }
        });
      }

      if (genAI.stop_loss) {
        annotations.yaxis.push({
          y: parseFloat(genAI.stop_loss),
          borderColor: '#FF4560',
          strokeDashArray: 4,
          label: {
            borderColor: '#FF4560',
            style: { color: '#fff', background: '#FF4560', fontSize: '10px' },
            text: `Stop: $${parseFloat(genAI.stop_loss).toFixed(2)}`
          }
        });
      }

      return annotations;
    }

    // Position is selected - show position annotations
    const pos = selectedPosition;
    const { adjustments, currentTarget, currentStop, originalTarget, originalStop } = selectedPositionData;

    // Entry price
    if (pos.entryPrice) {
      annotations.yaxis.push({
        y: pos.entryPrice,
        borderColor: '#FEB019',
        borderWidth: 2,
        strokeDashArray: 0,
        label: {
          borderColor: '#FEB019',
          style: { color: '#000', background: '#FEB019', fontSize: '10px' },
          text: `Entry: $${pos.entryPrice.toFixed(2)}`
        }
      });
    }

    // ✅ ORIGINAL TARGET (dashed green line)
    if (originalTarget && originalTarget > 0) {
      annotations.yaxis.push({
        y: originalTarget,
        borderColor: '#00E396',
        borderWidth: 2,
        strokeDashArray: 4,  // Dashed
        label: {
          borderColor: '#00E396',
          style: { color: '#000', background: '#00E396', fontSize: '10px' },
          text: `Original Target: $${originalTarget.toFixed(2)}`
        }
      });
    }

    // ✅ CURRENT TARGET (solid orange line) - only if adjusted
    if (adjustments.length > 0 && currentTarget && currentTarget !== originalTarget) {
      annotations.yaxis.push({
        y: currentTarget,
        borderColor: '#FFA500',  // Orange
        borderWidth: 3,
        strokeDashArray: 0,  // Solid
        label: {
          borderColor: '#FFA500',
          style: { 
            color: '#000', 
            background: '#FFA500', 
            fontSize: '10px',
            fontWeight: 'bold'
          },
          text: `Adjusted Target: $${currentTarget.toFixed(2)}`
        }
      });
      
      console.log('📍 Showing adjusted target:', { original: originalTarget, current: currentTarget });
    }

    // ✅ ORIGINAL STOP (dashed red line)
    if (originalStop && originalStop > 0) {
      annotations.yaxis.push({
        y: originalStop,
        borderColor: '#FF4560',
        borderWidth: 2,
        strokeDashArray: 4,  // Dashed
        label: {
          borderColor: '#FF4560',
          style: { color: '#fff', background: '#FF4560', fontSize: '10px' },
          text: `Original Stop: $${originalStop.toFixed(2)}`
        }
      });
    }

    // ✅ CURRENT STOP (solid crimson line) - only if adjusted
    if (adjustments.length > 0 && currentStop && currentStop !== originalStop) {
      annotations.yaxis.push({
        y: currentStop,
        borderColor: '#DC143C',  // Crimson
        borderWidth: 3,
        strokeDashArray: 0,  // Solid
        label: {
          borderColor: '#DC143C',
          style: { 
            color: '#fff', 
            background: '#DC143C', 
            fontSize: '10px',
            fontWeight: 'bold'
          },
          text: `Adjusted Stop: $${currentStop.toFixed(2)}`
        }
      });
      
      console.log('📍 Showing adjusted stop:', { original: originalStop, current: currentStop });
    }

    // Entry time vertical line
    if (pos.entryTime) {
      const entryTimestamp = new Date(pos.entryTime).getTime();
      
      annotations.xaxis.push({
        x: entryTimestamp,
        borderColor: '#FEB019',
        borderWidth: 2,
        strokeDashArray: 4,
        label: {
          borderColor: '#FEB019',
          style: { 
            color: '#000', 
            background: '#FEB019',
            fontSize: '10px',
            fontWeight: 'bold'
          },
          text: 'Entry',
          orientation: 'horizontal'
        }
      });

      // Entry point marker
      if (pos.entryPrice) {
        annotations.points.push({
          x: entryTimestamp,
          y: pos.entryPrice,
          marker: {
            size: 8,
            fillColor: '#FEB019',
            strokeColor: '#fff',
            strokeWidth: 2,
            shape: 'circle'
          }
        });
      }
    }

    // Exit time vertical line
    if (pos.exitTime) {
      const exitTimestamp = new Date(pos.exitTime).getTime();
      
      annotations.xaxis.push({
        x: exitTimestamp,
        borderColor: pos.isWin ? '#00E396' : '#FF4560',
        borderWidth: 2,
        strokeDashArray: 4,
        label: {
          borderColor: pos.isWin ? '#00E396' : '#FF4560',
          style: { 
            color: '#fff', 
            background: pos.isWin ? '#00E396' : '#FF4560',
            fontSize: '10px',
            fontWeight: 'bold'
          },
          text: 'Exit',
          orientation: 'horizontal'
        }
      });

      // Exit point marker
      if (pos.exitPrice) {
        annotations.points.push({
          x: exitTimestamp,
          y: pos.exitPrice,
          marker: {
            size: 8,
            fillColor: pos.isWin ? '#00E396' : '#FF4560',
            strokeColor: '#fff',
            strokeWidth: 2,
            shape: 'circle'
          }
        });
      }
    }

    // ✅ ADD ALL ADJUSTMENT MARKERS FROM ALL POSITIONS (show on main chart always)
    if (allAdjustmentsData && allAdjustmentsData.length > 0) {
      console.log(`📍 Adding ${allAdjustmentsData.length} adjustment markers from all positions to chart`);
      
      allAdjustmentsData.forEach((adj, index) => {
        console.log(`  - Marker ${index + 1}: timestamp=${adj.timestamp}, price=${adj.price}, newTarget=${adj.newTarget}`);
        
        // Add X-axis line at adjustment time
        annotations.xaxis.push({
          x: adj.timestamp,
          borderColor: '#FFA500',
          strokeDashArray: 2,
          borderWidth: 1,
          label: {
            borderColor: '#FFA500',
            style: { 
              color: '#000', 
              background: '#FFA500',
              fontSize: '9px'
            },
            text: '🔧',
            orientation: 'horizontal',
            offsetY: 0
          }
        });
        
        // Add marker at adjustment point using the close price
        annotations.points.push({
          x: adj.timestamp,
          y: adj.price,  // Use close price, not target
          marker: {
            size: 10,
            fillColor: '#FFA500',
            strokeColor: '#fff',
            strokeWidth: 2,
            shape: 'circle'
          },
          label: {
            borderColor: '#FFA500',
            style: {
              fontSize: '10px',
              color: '#fff',
              background: '#FFA500'
            },
            text: `Target: ${adj.newTarget.toFixed(2)}`
          }
        });
      });
      
      console.log(`✅ Added ${annotations.points.length} point annotations and ${annotations.xaxis.length} x-axis lines`);
    }

    // ✅ IF POSITION SELECTED: Add additional position-specific markers
    if (selectedPosition && adjustments && adjustments.length > 0) {
      console.log(`📍 Adding ${adjustments.length} position-specific adjustment markers`);
      
      adjustments.forEach((adj, index) => {
        // Add larger marker with label for selected position
        annotations.points.push({
          x: adj.timestamp,
          y: adj.newTarget,
          marker: {
            size: 12,
            fillColor: '#FF6B00',
            strokeColor: '#fff',
            strokeWidth: 3,
            shape: 'circle'
          },
          label: {
            text: `Adj #${index + 1}`,
            offsetY: -20,
            style: {
              fontSize: '10px',
              color: '#000',
              background: '#FFA500',
              padding: {
                left: 4,
                right: 4,
                top: 2,
                bottom: 2
              }
            }
          }
        });
      });
    }

    return annotations;
  }, [selectedPosition, genAI, selectedPositionData, allAdjustmentsData]);

  // Chart options
  const chartOptions = useMemo(() => ({
    chart: {
      type: "candlestick",
      height: 400,
      background: '#000000',
      foreColor: '#ffffff',
      zoom: {
        enabled: true,
        type: "x",
      },
      animations: {
        enabled: false,
      },
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true
        }
      }
    },
    theme: {
      mode: 'dark'
    },
    grid: {
      show: true,
      borderColor: '#1a1a1a',
      strokeDashArray: 3,
      xaxis: {
        lines: {
          show: true
        }
      },
      yaxis: {
        lines: {
          show: false
        }
      }
    },
    plotOptions: {
      candlestick: {
        colors: {
          upward: "#0d6efd",
          downward: "#dc3545"
        },
        wick: {
          useFillColor: true
        }
      },
    },
    stroke: {
      width: [1, 2, 2, 2],
      curve: 'smooth',
      colors: ['#999999', '#00b746', '#ef403c', '#FFA500']
    },
    markers: {
      size: [0, 4, 4, 4],
      strokeWidth: 1,
      strokeColors: '#fff',
      hover: {
        size: 6,
      }
    },
    xaxis: {
      type: "datetime",
      labels: {
        formatter: function (val) {
          return new Date(val).toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "numeric",
            hour12: false,
          });
        },
        style: {
          colors: '#b0b0b0',
          fontSize: '11px'
        }
      },
      axisBorder: {
        show: true,
        color: '#333333'
      },
      axisTicks: {
        show: true,
        color: '#333333'
      }
    },
    yaxis: {
      tooltip: {
        enabled: true,
      },
      labels: {
        formatter: (value) => {
          if (value === null || value === undefined) return '';
          return "$" + value.toFixed(2);
        },
        style: {
          colors: '#b0b0b0',
          fontSize: '11px'
        }
      },
      axisBorder: {
        show: true,
        color: '#333333'
      }
    },
    tooltip: {
      enabled: true,
      theme: 'dark',
      shared: true,
      intersect: false,  // ✅ Show tooltip without needing to hover exactly on point
      followCursor: true,  // ✅ Tooltip follows cursor
      style: {
        fontSize: '12px'
      },
      x: {
        show: true,
        format: 'HH:mm:ss'
      },
      y: {
        formatter: function(value, { seriesIndex, dataPointIndex, w }) {
          if (value === null || value === undefined) return '';
          return "$" + value.toFixed(2);
        }
      },
      custom: function({series, seriesIndex, dataPointIndex, w}) {
        // For candlestick series
        if (seriesIndex === 0) {
          const o = w.globals.seriesCandleO[seriesIndex][dataPointIndex];
          const h = w.globals.seriesCandleH[seriesIndex][dataPointIndex];
          const l = w.globals.seriesCandleL[seriesIndex][dataPointIndex];
          const c = w.globals.seriesCandleC[seriesIndex][dataPointIndex];
          
          return `<div class="px-3 py-2" style="background: #1a1a1a; border: 1px solid #333;">
            <div><strong>Open:</strong> ${o?.toFixed(2) || 'N/A'}</div>
            <div><strong>High:</strong> ${h?.toFixed(2) || 'N/A'}</div>
            <div><strong>Low:</strong> ${l?.toFixed(2) || 'N/A'}</div>
            <div><strong>Close:</strong> ${c?.toFixed(2) || 'N/A'}</div>
          </div>`;
        }
        
        return '';
      }
    },
    legend: {
      show: true,
      position: 'top',
      labels: {
        colors: '#ffffff'
      },
      markers: {
        width: 12,
        height: 12
      }
    },
    fill: {
      type: ['solid', 'gradient', 'gradient', 'gradient'],
      gradient: {
        shade: 'dark',
        type: 'vertical',
        shadeIntensity: 0.4,
        gradientToColors: ['#0d6efd', '#00b746', '#ef403c', '#FFA500'],
        inverseColors: false,
        opacityFrom: 0.8,
        opacityTo: 0.3,
        stops: [0, 100]
      }
    },
    annotations: positionAnnotations
  }), [positionAnnotations]);

  // Build series
  const series = useMemo(() => [
    { 
      name: "Price", 
      type: "candlestick", 
      data: ohlc 
    },
    { 
      name: "Long Signal", 
      type: "line",
      data: up,
      color: "#00b746"
    },
    { 
      name: "Short Signal", 
      type: "line",
      data: down,
      color: "#ef403c"
    },
    { 
      name: "Target Price", 
      type: "line",
      data: pv,
      color: "#FFA500"
    }
  ], [ohlc, up, down, pv]);

  // Loading state
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <Card className="shadow-sm border-0" style={{ 
        background: '#000000',
        border: '1px solid #1a1a1a'
      }}>
        <Card.Body className="p-3">
          <div className="h-100 d-flex align-items-center justify-content-center" style={{ height: '400px' }}>
            <div className="text-center" style={{ color: '#666666' }}>
              <div className="spinner-border spinner-border-sm mb-2" role="status"></div>
              <div>Loading market data...</div>
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  }

  const isClosed = selectedPosition && selectedPosition.exitTime;
  const { adjustments, currentTarget, currentStop, originalTarget, originalStop } = selectedPositionData;

  return (
    <Card className="shadow-sm border-0" style={{ 
      background: '#000000',
      border: '1px solid #1a1a1a'
    }}>
      <Card.Body className="p-3">
        {/* Position info header */}
        {selectedPosition && (
          <div className="mb-3 p-3 rounded" style={{ 
            backgroundColor: '#1a1a1a',
            border: '1px solid #333333'
          }}>
            <div className="row align-items-center">
              <div className="col-6">
                <div className="d-flex align-items-center">
                  <strong className="me-2 text-white">{selectedPosition.symbol}</strong>
                  <Badge bg={selectedPosition.isLong ? "success" : "danger"}>
                    {selectedPosition.isLong ? "LONG" : "SHORT"}
                  </Badge>
                  {isClosed && (
                    <Badge bg={selectedPosition.isWin ? "success" : "danger"} className="ms-2">
                      {selectedPosition.isWin ? "WIN" : "LOSS"}
                    </Badge>
                  )}
                  {/* ✅ Show adjustment count badge */}
                  {adjustments.length > 0 && (
                    <Badge bg="warning" className="ms-2">
                      {adjustments.length} Adjustment{adjustments.length > 1 ? 's' : ''}
                    </Badge>
                  )}
                </div>
                <small className="text-muted d-block mt-1">
                  Entry: ${selectedPosition.entryPrice?.toFixed(2)} • 
                  {isClosed && ` Exit: $${selectedPosition.exitPrice?.toFixed(2)} •`}
                  {selectedPosition.unrealizedPL && ` P&L: $${selectedPosition.unrealizedPL.toFixed(2)}`}
                </small>
                {/* ✅ Show adjustment details */}
                {adjustments.length > 0 && currentTarget && currentStop && (
                  <small className="text-warning d-block mt-1">
                    Target: ${originalTarget.toFixed(2)} → ${currentTarget.toFixed(2)} | 
                    Stop: ${originalStop.toFixed(2)} → ${currentStop.toFixed(2)}
                  </small>
                )}
              </div>
              <div className="col-6 text-end">
                <small className="text-muted">
                  {new Date(selectedPosition.entryTime).toLocaleString("en-US", {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </small>
              </div>
            </div>
          </div>
        )}

        {/* GenAI signal info (when no position selected) */}
        {!selectedPosition && genAI.RecommendedAction && (
          <div className="mb-3 p-2 rounded" style={{ 
            backgroundColor: '#1a1a1a',
            border: '1px solid #333333'
          }}>
            <div className="row align-items-center">
              <div className="col-6">
                <div className="d-flex align-items-center gap-2">
                  <span className="text-white">AI Signal:</span>
                  <Badge bg={
                    genAI.RecommendedAction === 'BUY' ? 'success' : 
                    genAI.RecommendedAction === 'SELL' ? 'danger' : 'secondary'
                  }>
                    {genAI.RecommendedAction}
                  </Badge>
                  <small className="text-muted">
                    {genAI.Confidence || 0}%
                  </small>
                </div>
              </div>
              <div className="col-6 text-end">
                {genAI.entry_price && (
                  <small className="text-muted">
                    Entry: ${genAI.entry_price} • 
                    {genAI.take_profit && ` Target: $${genAI.take_profit} •`}
                    {genAI.stop_loss && ` Stop: $${genAI.stop_loss}`}
                  </small>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Chart title */}
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="d-flex align-items-center gap-2">
            <h6 className="mb-0 text-white">Live Market Chart</h6>
            <Badge bg="success" className="ms-2">
              {data.length} bars
            </Badge>
            {/* ✅ Show total adjustments count on main chart */}
            {allAdjustmentsData.length > 0 && !selectedPosition && (
              <Badge bg="warning" className="ms-2">
                {allAdjustmentsData.length} Total Adjustment{allAdjustmentsData.length > 1 ? 's' : ''}
              </Badge>
            )}
          </div>
        </div>
        
        {/* Main chart */}
        <div style={{ background: '#000000' }}>
          <ReactApexChart
            options={chartOptions}
            series={series}
            type="candlestick"
            height={400}
          />
        </div>

        {/* Chart controls */}
        <div className="mt-2 d-flex justify-content-between align-items-center">
          <ButtonGroup size="sm">
            <Button variant="outline-secondary" active style={{ color: '#ffffff', borderColor: '#333333' }}>1m</Button>
            <Button variant="outline-secondary" disabled style={{ color: '#666666', borderColor: '#333333' }}>5m</Button>
            <Button variant="outline-secondary" disabled style={{ color: '#666666', borderColor: '#333333' }}>15m</Button>
          </ButtonGroup>
          
          <small className="text-muted">
            {selectedPosition ? '🔍 Position View' : '🤖 AI View'} • {data.length} bars
          </small>
        </div>
      </Card.Body>
    </Card>
  );
};

export default memo(MarketChart);

/*
 * ✅ WHAT THIS DOES:
 * 
 * 1. Shows ALL adjustments from ALL positions on the main chart (orange circles with wrench emoji)
 * 2. When a position is selected, highlights that position's adjustments with larger circles
 * 3. Reads adjustments DIRECTLY from bars (like EvaluationDetailModal)
 * 4. Looks for bars with eval_decision === 'ADJUST'
 * 5. Extracts eval_new_target and eval_new_stop_loss
 * 6. Shows original (dashed) and current (solid) lines for selected position
 * 7. Shows adjustment count badge
 * 8. Displays original → current values in header
 * 
 * ALWAYS VISIBLE: Orange wrench markers (🔧) for all position adjustments
 * WHEN SELECTED: Larger orange circles with labels (Adj #1, Adj #2) + target/stop lines
 * 
 * NO COMPLEX ENRICHMENT NEEDED - just reads directly from bar data!
 */